import { AnnotationPlane } from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';
import type { Environment } from '~/engine/data/Environment';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    frameRotationShowingPlane,
    frameShowsPlane,
    imageToPaneFrame,
} from '~/engine/view/planeViewGeometry';
import type { ViewManager } from '~/engine/view/ViewManager';

// Turns the row's frame onto the plane unless it already shows it (so a set pan is kept).
function showPlaneOnEnvironment(
    viewManager: ViewManager,
    environment: Environment,
    plane: AnnotationPlane,
    grid: NonNullable<Environment['image']>['grid']
): void {
    if (
        frameShowsPlane(viewManager.frameRotationOf(environment), plane, grid)
    ) {
        return;
    }
    viewManager.setFrameRotation(
        environment,
        frameRotationShowingPlane(plane, grid)
    );
    // The turn pivots on the crosshair, which has just moved, so each tile is
    // centred on its image afterwards.
    const image = environment.image;
    if (!image) {
        return;
    }
    for (const view of viewManager.viewsOf(environment)) {
        view.centerOn(image.boundingBox);
        view.updateView();
    }
}

// A row, and the anchor and plane in its frame.
interface GoToTarget {
    readonly environment: Environment;
    readonly grid: NonNullable<Environment['image']>['grid'];
    readonly anchorMm: PointMm;
    readonly plane: AnnotationPlane;
}

// The series' own row when a tile draws it as primary, else the first row fusing it as secondary.
function goToTargetOf(
    viewManager: ViewManager,
    plane: AnnotationPlane,
    anchorMm: PointMm
): GoToTarget | undefined {
    const seriesUid = plane.seriesInstanceUid;
    const ownRow = viewManager.environmentShowingSeries(seriesUid);
    const ownGrid = ownRow?.image?.grid;
    if (
        ownRow &&
        ownGrid &&
        viewManager
            .viewsOf(ownRow)
            .some((view) => paneImageOf(view).kind === 'primary')
    ) {
        return {
            environment: ownRow,
            grid: ownGrid,
            anchorMm,
            plane,
        };
    }
    for (const environment of getDatasetEnvironmentRegistry().residentEnvironments()) {
        const grid = environment.image?.grid;
        if (!grid) {
            continue;
        }
        for (const view of viewManager.viewsOf(environment)) {
            const pane = paneImageOf(view);
            if (pane.kind === 'secondary' && pane.seriesUid === seriesUid) {
                const ontoPane = imageToPaneFrame(pane);
                return {
                    environment,
                    grid,
                    anchorMm: ontoPane.point(anchorMm),
                    plane: ontoPane.plane(plane),
                };
            }
        }
    }
    return undefined;
}

export function goToPlanePoint(
    viewManager: ViewManager,
    plane: AnnotationPlane,
    anchorMm: PointMm
): boolean {
    const target = goToTargetOf(viewManager, plane, anchorMm);
    if (!target) {
        return false;
    }
    // Move the crosshair first: it decides which slice each tile shows, and
    // the turn rotates about it.
    target.environment.updatePlaneIntersectionPoint(target.anchorMm);
    showPlaneOnEnvironment(
        viewManager,
        target.environment,
        target.plane,
        target.grid
    );
    return true;
}
